import { useMemo, useState } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import EmptyState from '@/components/base/EmptyState';
import ReceiptForm from './components/ReceiptForm';
import ReceiptPreview from './components/ReceiptPreview';
import { useReceipts } from '@/hooks/useReceipts';
import { useProjects } from '@/hooks/useProjects';
import { useToast } from '@/components/feature/ToastProvider';
import { downloadReceiptPdf, toReceiptPdfData } from '@/lib/receiptPdf';
import { formatCurrency, formatDate } from '@/lib/format';
import type { Receipt } from '@/types';

export default function BillingPage() {
  const { notify } = useToast();
  const { receipts, loading, error, reload } = useReceipts();
  const { projects } = useProjects();

  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState<Receipt | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return receipts.filter((receipt) => {
      const matchProject = !projectFilter || receipt.project_id === projectFilter;
      const matchFrom = !dateFrom || receipt.receipt_date >= dateFrom;
      const matchTo = !dateTo || receipt.receipt_date <= dateTo;
      const matchTerm =
        !term ||
        receipt.receipt_number.toLowerCase().includes(term) ||
        (receipt.projects?.name || '').toLowerCase().includes(term) ||
        (receipt.projects?.clients?.name || '').toLowerCase().includes(term) ||
        (receipt.created_by_name || '').toLowerCase().includes(term);
      return matchProject && matchFrom && matchTo && matchTerm;
    });
  }, [receipts, search, projectFilter, dateFrom, dateTo]);

  const totalAmount = useMemo(
    () => filtered.reduce((sum, receipt) => sum + Number(receipt.amount), 0),
    [filtered],
  );

  const handleDownload = async (receipt: Receipt) => {
    try {
      await downloadReceiptPdf(toReceiptPdfData(receipt, receipt.created_by_name || 'Usuario'));
      notify({ tone: 'success', title: 'Recibo descargado', message: `${receipt.receipt_number}.pdf` });
    } catch {
      notify({ tone: 'error', title: 'No se pudo descargar', message: 'Intente nuevamente.' });
    }
  };

  const projectOptions = projects.map((project) => ({ value: project.id, label: project.name }));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">Recibos</h2>
          <p className="text-sm text-foreground-500">
            {filtered.length} de {receipts.length} recibos · Total {formatCurrency(totalAmount)}
          </p>
        </div>
        <Button icon="ri-add-line" onClick={() => setFormOpen(true)}>
          Nuevo recibo
        </Button>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5 lg:flex-row lg:items-end">
        <div className="relative w-full lg:max-w-xs">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por N° recibo, cliente, proyecto o usuario..."
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
        </div>

        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-3 lg:items-end">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label">
              Proyecto
            </label>
            <select
              value={projectFilter}
              onChange={(event) => setProjectFilter(event.target.value)}
              className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 lg:w-48"
            >
              <option value="">Todos los proyectos</option>
              {projectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label">
              Desde
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label">
              Hasta
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <Loading label="Cargando recibos..." />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-receipt-line"
          title={receipts.length === 0 ? 'No hay recibos registrados' : 'Sin resultados'}
          description={
            receipts.length === 0
              ? 'Genere su primer recibo para comenzar a controlar la facturación.'
              : 'Ajuste la búsqueda o los filtros aplicados.'
          }
          action={
            receipts.length === 0 ? (
              <Button icon="ri-add-line" onClick={() => setFormOpen(true)}>
                Nuevo recibo
              </Button>
            ) : (
              <Button
                variant="outline"
                icon="ri-refresh-line"
                onClick={() => {
                  setSearch('');
                  setProjectFilter('');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Limpiar filtros
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">N° Recibo</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Fecha</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Cliente</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Proyecto</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">Monto</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Generado por</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((receipt) => (
                  <tr key={receipt.id} className="border-b border-background-200 last:border-0 transition-colors hover:bg-background-100/50">
                    <td className="px-4 py-3 text-sm font-semibold text-foreground-900">
                      {receipt.receipt_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">{formatDate(receipt.receipt_date)}</td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      {receipt.projects?.clients?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      {receipt.projects?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-foreground-900">
                      {formatCurrency(receipt.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      {receipt.created_by_name || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone="primary" icon="ri-check-double-line">
                        Emitido
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPreviewReceipt(receipt)}
                          title="Vista previa"
                          aria-label={`Vista previa del recibo ${receipt.receipt_number}`}
                          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                        >
                          <i className="ri-eye-line text-base"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(receipt)}
                          title="Descargar PDF"
                          aria-label={`Descargar recibo ${receipt.receipt_number}`}
                          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
                        >
                          <i className="ri-download-2-line text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ReceiptForm
        open={formOpen}
        projects={projects}
        onClose={() => setFormOpen(false)}
        onCreated={() => {
          setFormOpen(false);
          reload();
        }}
      />

      <ReceiptPreview receipt={previewReceipt} onClose={() => setPreviewReceipt(null)} />
    </div>
  );
}